<i18n>
  {
    "fr": {
      "legend-title": "Légende",
      "value": "",
      "spring-level": "Niveau",
      "waterLevel": "Hauteur d'eau",
      "sfl": "Profondeur",
      "head-pressure": "Hauteur d'eau",
      "available-parameter": "Paramètre mesuré",
      "avgValue": "Moyenne",
      "minValue": "Minimum",
      "maxValue": "Maximum",
      "value": "",
      "warning-threshold-title": "Seuils d'alerte",
      "axis-range-default": "Par défaut",
      "axis-range-validated": "Données validées",
      "axis-range-auto": "Auto",
      "axis-range-y": "Limites axe Y (min / max)",
      "axis-range-info": {
        "default": "<b>Par défaut</b>: Limites de l'axe Y définies sur les bornes min et max de l'ensemble de la chronique de mesures.",
        "validated": "<b>Données validées</b>: Limites de l'axe Y définies sur les bornes min et max des données validées.",
        "auto": "<b>Auto</b>: Limites de l'axe Y définies sur les bornes min et max des données actuellement affichées dans le graphique."
      },
      "common": {
        "close": "Fermer"
      }
    },
    "de": {
  
    }
  }
  </i18n>
  
  <template>
    <div class="wrapper groundwater-level" v-if="show">
      <div class="graph-option selected-level-parameter">
        <md-field class="option" v-if="showParameterTypeSelect">
          <md-select v-model="selectedLevelParameter" @md-selected="changeLevelParameter">
            <md-option value="WATER_LEVEL" v-if="hasWaterLevel">{{ $t('waterLevel') }}</md-option>
          </md-select>
        </md-field>
        <md-field class="option axis-range" v-if="showAxisRangeSelect">
          <label @click="showAxisRangeInfo = true">{{ $t('axis-range-y') }} <md-icon class="label-icon clickable">help</md-icon></label>
          <md-select v-model="selectedAxisRangeMode" @md-selected="refreshGraph">
            <md-option value="default">{{ $t('axis-range-default') }}</md-option>
            <md-option value="validated">{{ $t('axis-range-validated') }}</md-option>
            <md-option value="auto">{{ $t('axis-range-auto') }}</md-option>
          </md-select>
        </md-field>
      </div>
  
      <div class="data-graph">
        <plotly
          v-if="showGraph"
          :data="graphData"
          :layout="graphLayout"
          @mouse-over="onMouseHover"
          @mouse-out="onMouseOut"
          :mouseOverValue="highlightedTimestamp"
          ref="plotly"
        />
  
        <div class="loading" v-if="loading">
          <md-progress-spinner class="loading-animation" :md-diameter="40" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
        </div>
      </div>
  
      <div class="graph-option warning-thresholds-container" v-if="showThreshold && warningThresholds.length > 0">
        <div class="warning-threshold-title">{{ $t('warning-threshold-title') }}</div>
        <div :class="['threshold', {'disabled': warningThreshold.disabled}]" v-for="warningThreshold in warningThresholds" :key="warningThreshold.warningState.id" @click="toggleThreshold(warningThreshold)">
          <div class="threshold-linecolor" v-if="!warningThreshold.disabled" :style="{'background-color': warningThreshold.warningState.color}"></div>
          <div class="threshold-title">{{ warningThreshold.warningState.label }}</div>
          <md-tooltip>
            {{ warningThreshold.value }} {{ currentUnit.symbol[$i18n.locale] }}
            <span v-if="warningThreshold.recurrenceInterval !== null"> - {{ getRecurrenceIntervalPrefix(warningThreshold) }}{{ warningThreshold.recurrenceInterval }}</span>
          </md-tooltip>
        </div>
      </div>
  
      <md-dialog :md-active.sync="showAxisRangeInfo" class="axis-range-info">
        <md-dialog-content>
          <ul>
            <li v-html="$t('axis-range-info.default')"></li>
            <li v-html="$t('axis-range-info.validated')"></li>
            <li v-html="$t('axis-range-info.auto')"></li>
          </ul>
        </md-dialog-content>
  
        <md-dialog-actions>
          <md-button class="md-primary" @click="showAxisRangeInfo = false">{{ $t('common.close') }}</md-button>
        </md-dialog-actions>
      </md-dialog>
    </div>
  </template>
  
  <script>
  
  import _ from 'lodash'
  import moment from 'moment'
  import { timeFormat } from 'd3'
  
  import CommonMixin from '@/mixins/store/common'
  import TimeMixin from '@/mixins/store/time'
  import GroundwaterGraphMixin from '@/mixins/graph/groundwaterGraph'
  import { PlotlyData, PlotlyXAxis, PlotlyYAxis, PlotlyLayout } from '@/utils/plotly'
  import ForecastLevelProxy from '@/proxies/forecastLevel'
  import ThresholdProxy from '@/proxies/threshold'
  
  export default {
    name: 'MountainSpringLevel',
    mixins: [
      CommonMixin,
      TimeMixin,
      GroundwaterGraphMixin
    ],
    props: {
      showForecast: {
        type: Boolean,
        default: false
      },
      forecastReferenceEvent: {
        type: Object,
        default: null
      },
      showParameterTypeSelect: {
        type: Boolean,
        default: true
      },
      showThreshold: {
        type: Boolean,
        default: false
      },
      showThresholdLabel: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        selectedLevelParameter: 'WATER_LEVEL',
        color: '#184aa0',
        warningThresholds: [],
        showAxisRangeInfo: false
      }
    },
    computed: {
      graphLayout () {
        let toReturn = new PlotlyLayout()
        toReturn.xaxis = new PlotlyXAxis()
        toReturn.xaxis.isDate()
        toReturn.xaxis.tickformat = this.tickformat
        toReturn.xaxis.hoverformat = this._hoverformat
        toReturn.xaxis.tickangle = '0'
        toReturn.xaxis.range = [this._dateFrom, this._dateTo]
        if (this.showTickRangeOnly) {
          toReturn.xaxis.tickvals = this.generateTickVals()
          toReturn.xaxis.ticktext = this.generateTickTexts()
        }
        toReturn.xaxis.visible = this.isXaxisVisible
        toReturn.xaxis.fixedrange = this.fixedRange
  
        toReturn.yaxis = new PlotlyYAxis(this.measuredTypeName(this.selectedLevelParameter))
        toReturn.yaxis.range = this.getAxisRange(this.selectedLevelParameter)
  
        toReturn.yaxis.fixedrange = this.fixedRange
  
        if (this.selectedLevelParameter === 'WATER_LEVEL') {
          toReturn.lines([{value: 0, color: 'black'}])
          if (toReturn.yaxis.range !== null) toReturn.yaxis.range[1] = toReturn.yaxis.range[1] <= 1 ? 1 : toReturn.yaxis.range[1]
        }
  
        if (this.centerBarEvent !== null) {
          toReturn.shapes.push({
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: this.centerBarEvent.toISOString(),
            y0: 0,
            x1: this.centerBarEvent.toISOString(),
            y1: 1,
            line: {
              width: 1
            }
          })
        }
  
        let enabledWarningThresholds = this.warningThresholds.filter((warningThreshold) => !warningThreshold.disabled)
        if (this.showThreshold && enabledWarningThresholds.length > 0) {
          let thresholdsToSet = []
          enabledWarningThresholds.forEach((warningThreshold) => {
            thresholdsToSet.push({
              value: warningThreshold.value,
              color: warningThreshold.warningState.color,
              text: this.showThresholdLabel ? `${warningThreshold.value} ${this.currentUnit.symbol[this.$i18n.locale]} - ${warningThreshold.warningState.label}` : null
            })
  
            // Si le paramètre range a été défini sur l'axe y (n'est pas égal à null), il faut prendre en compte ces seuils dans la range
            if (toReturn.yaxis.range === null) return
            if (warningThreshold.value < toReturn.yaxis.range[0]) toReturn.yaxis.range[0] = warningThreshold.value
            if (warningThreshold.value > toReturn.yaxis.range[1]) toReturn.yaxis.range[1] = warningThreshold.value
          })
  
          toReturn.thresholds(thresholdsToSet)
        }
  
        toReturn.margin = this.margin
        toReturn.hovermode = 'x'
  
        return toReturn
      },
      hasWaterLevel () {
        return typeof this.datasources.find((datasource) => datasource.parameterType.code === 'WATER_LEVEL') !== 'undefined'
      },
      currentUnit () {
        if (this.timeseries.length === 0) return ''
        // SD Toutes les sources de données affichées traites du même paramètre
        let parameterType = this.timeseries.find((timeserie) => timeserie.datasource.parameterType.code === this.selectedLevelParameter).datasource.parameterType
        return parameterType.unit
      }
    },
    methods: {
      convertToGraphData (timeserie) {
        let color = this.getColor(timeserie.datasource)
  
        let xValues = timeserie.data.map((d) => this.formatMomentUtc(d.event))
  
        if (this._aggregationMode === 'raw') {
          let toReturn = new PlotlyData(timeserie.datasource.datasourceType.name[this.$i18n.locale], xValues, timeserie.data.map((d) => d.value), null, color)
          toReturn.hoverinfo = 'x+y'
          toReturn.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}]</extra>`
          toReturn.showlegend = this.showLegends
          toReturn.line.width = 2
          return toReturn
        }
  
        // let min = new PlotlyData(`${timeserie.datasource.datasourceType.name[this.$i18n.locale]} - ${this.$t('min')}`, xValues, timeserie.data.map((d) => d.minValue), null)
        // min.isZoneMin()
        // min.legendgroup = timeserie.datasource.datasourceType.code
        // min.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}] ${this.$i18n.t('min')}</extra>`
        // min.showlegend = this.showLegends
  
        let selectedValue = this.selectedValue
        
        let avg = new PlotlyData(`${timeserie.datasource.datasourceType.name[this.$i18n.locale]} - ${this.$t(selectedValue)}`, xValues, timeserie.data.map((d) => d[selectedValue]), null)
        // avg.isZoneMedian()
        avg.legendgroup = timeserie.datasource.datasourceType.code
        avg.line.color = color
        avg.line.width = 2
        avg.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}] ${this.$i18n.t(selectedValue)}</extra>`
        avg.showlegend = this.showLegends
  
        if (timeserie.datasource.parameterType.code === 'HEADS_FORECAST_7') avg.showPoint()
  
        // let max = new PlotlyData(`${timeserie.datasource.datasourceType.name[this.$i18n.locale]} - ${this.$t('max')}`, xValues, timeserie.data.map((d) => d.maxValue), null)
        // max.isZoneMax()
        // max.legendgroup = timeserie.datasource.datasourceType.code
        // max.hovertemplate = `%{y:.2f} <extra>[${timeserie.datasource.parameterType.unit.symbol[this.$i18n.locale]}] ${this.$i18n.t('max')}</extra>`
        // max.showlegend = this.showLegends
  
        return [
          // min,
          avg,
          // max
        ]
      },
      isDatasourceDisplayable (datasource) {
        // On ne prend en compte que les paramètres de niveaux (HEAD_BSL et HEAD_SFL)
        return !datasource.datasourceType.webviewConfig.hidden &&
        datasource.parameterType.code === this.selectedLevelParameter &&
        _.includes(this.datasourceTypes, datasource.datasourceType.code) &&
        datasource.stats.minDate !== null
      },
      fetchTimeseries () {
        return new Promise((resolve,reject) => {
          this.timeseries = []
          let toFetch = []
  
          // Filtrer les sources de données à afficher:
          // - Il ne faut pas de flag "hidden"
          // - Il faut que le paramètre soit de type 'HEAD_BSL' ou 'HEAD_SFL'
          let datasources = this.datasources.filter((datasource) => this.isDatasourceDisplayable(datasource))
  
          datasources.forEach((datasource) => toFetch.push(this.getData(datasource.id, this._dateFrom, this._dateTo)))
  
          Promise.all(toFetch).then((timeseries) => {
            timeseries.forEach((timeserie, index) => {
              this.timeseries.push({
                datasource: datasources[index],
                data: timeserie
              })
            })
  
            // Récupération des données annexes
            toFetch = [
              this.fetchForecast(),
              // this.fetchWarningThreshold()
            ]
  
            Promise.all(toFetch).then(resolve).catch(reject)
          }).catch(reject)
        })
      },
      fetchForecast () {
        return new Promise((resolve, reject) => {
          // Récupération des données de prévisions si nécessaire
          if (this.showForecast && this.forecastReferenceEvent !== null) {
            ForecastLevelProxy.fetchForecast(this.observationPoint, this.forecastReferenceEvent).then((data) => {
              // On tronque les prévisions pour qu'elles ne débordent pas sur la partie gauche du graphique
              // Les valeurs sont stockés à 00:00 UTC+1 (donc 23:00 UTC). L'aggregation temporelle "journalière" se fait en UTC en base de donnée.
              // Donc le timetamp à 23:00 UTC est compté pour le jour avant.
              let minForecastEvent = this.centerBarEvent.clone().subtract(1, 'days')
              // Informations sur la source de données reprises sur la première datasources de prévisions
              this.timeseries.push({
                datasource: this.observationPoint.datasources.find((datasource) => datasource.parameterType.code === 'HEADS_FORECAST_7'),
                data: data.filter((d) => moment.utc(d.event).isSameOrAfter(minForecastEvent))
              })
              resolve()
            }).catch(reject)
          } else {
            resolve()
          }
        })
      },
      fetchWarningThreshold () {
        return new Promise((resolve, reject) => {
          this.warningThresholds = []
  
          if (this.showThreshold) {
            // Récupération de la source de donnée qui est affichée.
            // Les seuils sont assignées aux sources de données avec télétransmission.
            let datasource = this.datasources.find((datasource) => this.isDatasourceDisplayable(datasource) && datasource.datasourceType.code === 'remote')
            if (typeof datasource === 'undefined') return resolve()
  
            ThresholdProxy.listWarningThresholds(datasource.id).then((warningThresholds) => {
              this.warningThresholds = warningThresholds
              resolve()
            }).catch((e) => {
              // Pas de seuils, on termine ici sans erreur.
              if (e.response.status === 404) return resolve()
              reject(e)
            })
          } else {
            resolve()
          }
        })
      },
      updateTimeline () {
        let events = []
        // On affiche uniquement la timeline de HEAD_BSL, étant donné que ce sont les mêmes entre HEAD_BSL et HEAD_SFL
        this.datasources.filter((ds) => { return this.isDatasourceDisplayable(ds) }).forEach((datasource) => events.push({
          id: datasource.id,
          timelineId: datasource.parameterType.id,
          timelineName: this.$t('spring-level'),
          eventName: datasource.datasourceType.name[this.$i18n.locale],
          start: this.momentUtc(datasource.stats.minDate).utcOffset(0, true), // HACK: On "écrase" la timezone pour les données de la timeline: les données sont notés UTC mais comporte déjà le changement userUtcOffset
          end: this.momentUtc(datasource.stats.maxDate).utcOffset(0, true),   // HACK: On "écrase" la timezone pour les données de la timeline: les données sont notés UTC mais comporte déjà le changement userUtcOffset
          color: this.getColor(datasource), 
          order: datasource.parameterType.order
        }))
        this.addEvents(events)
      },
      changeLevelParameter () {
        this.updateData()
      },
      generateTickVals() {
        if (typeof this.tickBarRange === 'undefined') return [this._dateFrom, this._dateTo]
        let toReturn = [this._dateFrom]
        let tmp = moment(this._dateFrom)
  
        while (tmp.isBefore(this._dateTo)) {
          tmp = _.clone(tmp.add(this.tickBarRange.size, this.tickBarRange.unit))
          toReturn.push(tmp.toISOString())
        }
        toReturn.push(this._dateTo)
        return toReturn
      },
      generateTickTexts () {
        if (typeof this.tickBarRange === 'undefined') return [timeFormat(this.tickformat)(new Date(this._dateFrom)), timeFormat(this.tickformat)(new Date(this._dateTo))]
        let toReturn = [timeFormat(this.tickformat)(new Date(this._dateFrom))]
        let tickVals = this.generateTickVals()
        for (let i = 0; i < tickVals.length - 2; i++) {
          toReturn.push('')
        }
        toReturn.push(timeFormat(this.tickformat)(new Date(this._dateTo)))
        return toReturn
      },
      toggleThreshold (threshold) {
        threshold.disabled = !threshold.disabled
        this.$set(this.warningThresholds, this.warningThresholds.indexOf(threshold), threshold)
        this.refreshGraph()
      },
      getRecurrenceIntervalPrefix (warningThreshold) {
        if (warningThreshold.thresholdType === 'statistical') return 'T'
        return ''
      }
    }
  }
  </script>
  
  <style lang="scss">
  
  </style>
  
  <style lang="scss" scoped>
    .graph-option {
      display: flex;
      margin-left: 100px;
      gap: 5px;
  
      &.selected-level-parameter {
        margin-bottom: -30px;
      }
  
      .option {
        max-width: 140px;
  
        &.md-field {
          margin-bottom: 0px !important;
        }
  
        &.axis-range {
          max-width: 167px;
        }
      }
    }
  
    .data-graph {
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      height: 264px;
    }
  
    .warning-thresholds-container {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
  
      .threshold {
        height: 30px;
        min-width: 55px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #d4d4d4;
        margin: 5px;
        cursor: pointer;
        margin-right: 5px;
        padding: 2px;
  
        &.disabled .threshold-title{
          margin-left: 5px;
        }
  
        .threshold-linecolor {
          width: 15px;
          height: 2px;
          margin-right: 10px;
          margin-left: 5px;
        }
      }
    }
  
    .axis-range-info {
      .md-content {
        padding: 25px;
      }
      ul {
        padding-left: 15px;
      }
    }
  </style>
  